import { CommonModule } from '@angular/common';
import { Component, inject, Inject, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatStepperModule } from '@angular/material/stepper';
import { AlertService } from 'src/app/core/services/alert.service';
import { from, of, switchMap } from 'rxjs';
import { LayoutService } from 'src/app/core/services/layout.service';
import { InspectionCobotService } from './inspection-cobot.service';
import { ItemCobot } from 'src/app/features/compression/view-cobot-item/types/item-cobot';
import { CobotUpdateManuallyCams } from 'src/app/features/compression/view-cobot/types/cobot-update-manually-cams';
import { Inspection } from 'src/app/api/elite/types/inspections';

interface InspectionCobotData {
  cobotItems: ItemCobot[];
  workOrderId: number;
  assemblyTitle: string;
  packingBoxId: number;
  inspection: Inspection;
}

interface InspectionCobotSteps {
  cobotId: string;
  cobotRunId: number;
  cobotCreatedAt: string;
  cavity: number;
  items: {
    name: string;
    position: 'UP' | 'DOWN';
    setManuallyError?: boolean;
    imageUrl: string;
  }[];
}

@Component({
  selector: 'app-inspection-cobot',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatCardModule,
    MatStepperModule,
  ],
  templateUrl: './inspection-cobot.component.html',
  styleUrls: ['./inspection-cobot.component.sass'],
  providers: [InspectionCobotService],
})
export class InspectionCobotComponent implements OnInit {
  constructor(
    public dialogRef: MatDialogRef<InspectionCobotComponent>,
    @Inject(MAT_DIALOG_DATA) public data: InspectionCobotData
  ) {}

  steps: InspectionCobotSteps[] = [];
  stepsAvailables: boolean = false;

  currentStep: number = 0;

  aqlAllCavitiesToUse: number = 0; //* This is percentage of the AQL
  inspectionId: number;

  private readonly alertService = inject(AlertService);
  private readonly layoutService = inject(LayoutService);
  private readonly inspectionCobotService = inject(InspectionCobotService);

  ngOnInit(): void {
    const allCavities: InspectionCobotSteps[] = this.cobotItems
      .map((cobotItem) =>
        cobotItem.cavities.map((cavity) => ({
          cobotId: cobotItem.id,
          cobotRunId: cobotItem.runId,
          cobotCreatedAt: cobotItem.createdAt,
          cavity: cavity.cavity,
          items: cavity.cams.map((i) => ({
            name: i.name,
            position: i.position,
            setManuallyError: null,
            imageUrl: i.imageUrl,
          })),
        }))
      )
      .flat();
    // const allCavities = cobots.map((cobot) => cobot.cavities).flat();

    this.aqlAllCavitiesToUse = this.inspection.visualDetails.quantity;

    this.shuffleArray(allCavities);

    this.steps = allCavities.slice(0, this.aqlAllCavitiesToUse);

    this.stepsAvailables = this.steps.length > 0;
  }

  private currentStepCavity(): InspectionCobotSteps {
    return this.steps[this.currentStep];
  }

  private currentStateCompleted(): boolean {
    const someCamWasNotInspected = this.currentStepCavity().items.some(
      (i) => i.setManuallyError === null
    );

    if (someCamWasNotInspected) {
      this.alertService.warning(
        'Por favor inspeccione todas las imagenes antes de continuar.',
        'Algunas imagenes no han sido inspeccionadas',
        {
          showCancelButton: false,
          confirmButtonText: 'Entendido',
        }
      );
    }

    return !someCamWasNotInspected;
  }

  nextStep() {
    if (!this.currentStateCompleted()) return;

    this.currentStep = Math.min(this.currentStep + 1, this.steps.length - 1);
  }

  previousStep() {
    this.currentStep = Math.max(this.currentStep - 1, 0);
  }

  getCurrentStep() {
    return this.currentStep;
  }

  stepIsCompleted(step: number): boolean {
    return this.currentStep === step;
  }

  onClose() {
    this.dialogRef.close();
  }

  shuffleArray(array: Array<any>): void {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
  }

  setManuallyImgStatus(item: InspectionCobotSteps['items'][0]): void {
    const currentStatus = item.setManuallyError;

    if (currentStatus === null) {
      item.setManuallyError = false;
    } else {
      item.setManuallyError = !currentStatus;
    }
  }

  get cobotItems(): ItemCobot[] {
    return this.data.cobotItems;
  }

  get inspection(): Inspection {
    return this.data.inspection;
  }

  onClickInspection(): void {
    if (!this.currentStateCompleted()) {
      return;
    }

    const piecesAsGood: number = this.steps.filter((step) =>
      step.items.every((item) => item.setManuallyError === false)
    ).length;

    const piecesAsBad: number = this.steps.filter((step) =>
      step.items.some((item) => item.setManuallyError === true)
    ).length;

    from(
      this.alertService.question(
        `Se inspeccionaron ${piecesAsGood} piezas como buenas y ${piecesAsBad} como malas.`,
        `¿Desea finalizar la inspección?`,
        {
          confirmButtonText: 'Enviar',
          showCancelButton: true,
          cancelButtonText: 'Cancelar',
        }
      )
    )
      .pipe(
        switchMap((res) => {
          if (!res.isConfirmed) {
            return of(null);
          }
          this.layoutService.setLoading(true);

          return this.inspectionCobotService.startFinishedCobotInspection({
            bodyCobotUpdate: this.getCobotUpdateManuallyCams(),
            workOrderId: this.data.workOrderId,
            quantity: this.aqlAllCavitiesToUse,
            quantityErrors: piecesAsBad,
            packingBoxId: this.data.packingBoxId,
            assemblyTitle: this.data.assemblyTitle,
            inspection: this.inspection,
          });
        })
      )
      .subscribe(
        (res) => {
          this.layoutService.setLoading(false);
          if (!res) {
            return;
          }
          this.dialogRef.close(res);
          this.alertService.success('La inspección se ha creado correctamente');
        },
        (err) => {
          this.layoutService.setLoading(false);
          this.dialogRef.close(null);
          this.alertService.error(err);
        }
      );
  }

  private getCobotUpdateManuallyCams(): CobotUpdateManuallyCams[] {
    const allCavitiesInspectioned = this.steps;

    //* Group all the cavities by his cobotId
    const groupByCobotId = allCavitiesInspectioned.reduce(
      (
        acc,
        item
      ): {
        [key: string]: CobotUpdateManuallyCams;
      } => {
        const cobotId = item.cobotId;
        if (!acc[cobotId]) {
          acc[cobotId] = {
            cobotId: item.cobotId,
            inspectionId: null,
            cavities: [
              {
                cavity: item.cavity,
                cams: item.items.map((cam) => ({
                  inspectionHasError: cam.setManuallyError,
                  name: cam.name,
                  position: cam.position,
                })),
              },
            ],
          };
          return acc;
        }

        acc[cobotId].cavities.push({
          cavity: item.cavity,
          cams: item.items.map((cam) => ({
            inspectionHasError: cam.setManuallyError,
            name: cam.name,
            position: cam.position,
          })),
        });

        return acc;
      },
      {}
    );

    return Object.values(groupByCobotId);
  }

  onDateParse(date: string): Date {
    return new Date(date);
  }
}
